import { UnifiedApiParams } from '@/types/mercury-data-types/unifiedapi';
import type {
  Signal,
  SignalsFetchResponse,
  SignalsSignalFetchResponse,
} from '@/types/mercury-data-types/signals';

import { findInObject } from '@/utils/findInObject';
import { unifiedApiKeys } from '../../../../services/keys';
import { getEntries } from '@/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { openapiClient, openApiFetch } from '@/lib/openapi-fetch';
import { CollectorType } from '@/client/features/signals/types';

const BASE_PATHS = {
  COMMON: 'common',
  SYSTEM: 'system',
  STRUCTURED: 'data.structured',
  CATALOG: 'catalog',
  METADATA: 'metadata',
  TRANSLATIONS: 'translations',
  ATTRIBUTES: 'attributes',
} as const;

export const SIGNALS_PATHS = {
  ID: ['signal_id', `${BASE_PATHS.METADATA}.id`],
  TRANSLATIONS: [BASE_PATHS.TRANSLATIONS],
  TITLE: [
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.title`,
    `${BASE_PATHS.COMMON}.title`,
    `${BASE_PATHS.STRUCTURED}.title`,
  ],
  SUMMARY_ONLY: [
    `${BASE_PATHS.COMMON}.quo_summary`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.summary`,
  ],
  FORUM_AUTHOR: [`${BASE_PATHS.STRUCTURED}.author.name`],
  AUTHOR_JOB_TITLE: [`${BASE_PATHS.STRUCTURED}.author.jobtitle`],
  TELEGRAM_AUTHOR: [`${BASE_PATHS.STRUCTURED}.Sender.User.Username`],
  LANGUAGE: [`${BASE_PATHS.STRUCTURED}.language`],
  THREAD_TITLE: [
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.threat.title`,
    `${BASE_PATHS.STRUCTURED}.thread.title`,
  ],
  THREAD_PATH: [`${BASE_PATHS.STRUCTURED}.thread.path`],
  CHANNEL_TITLE: [`${BASE_PATHS.STRUCTURED}.Channel.title`],
  AI_SUMMARY: [`${BASE_PATHS.COMMON}.quo_summary`],
  SUMMARY: [
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.summary`,
    `${BASE_PATHS.STRUCTURED}.content.summary`,
  ],
  TEXT: [
    `${BASE_PATHS.STRUCTURED}.content.text`,
    `${BASE_PATHS.COMMON}.quo_summary`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.summary`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.content.summary`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.content.text`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.Text`,
    `${BASE_PATHS.COMMON}.summary`,
    `${BASE_PATHS.STRUCTURED}.content.summary`,
    `${BASE_PATHS.STRUCTURED}.Text`,
  ],
  TRANSLATED_TEXT: [
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.content.text`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.content.Text`,
    `${BASE_PATHS.TRANSLATIONS}.en.${BASE_PATHS.STRUCTURED}.Text`,
  ],
  TIMESTAMP: [`${BASE_PATHS.COMMON}.timestamp`, `${BASE_PATHS.STRUCTURED}.timestamp`, '@timestamp'],
  COLLECTOR_NAME: [`${BASE_PATHS.METADATA}.collector.name`],
  PUBLISHED_AT: [`${BASE_PATHS.METADATA}.published_at`],
  UPDATED_AT: [`${BASE_PATHS.METADATA}.updated_at`],
  INGESTED_AT: [`${BASE_PATHS.METADATA}.ingested_at`],
  LINK: [`${BASE_PATHS.STRUCTURED}.site_url`, `${BASE_PATHS.METADATA}.link`],
  CATEGORY: [`${BASE_PATHS.CATALOG}.category`],
  VULNERABILITY_NAME: [`${BASE_PATHS.CATALOG}.vulnerability`],
  VULNERABILITY_ID: [`${BASE_PATHS.CATALOG}.vulnerability_id`],
  THREAT_ACTOR_NAME: [`${BASE_PATHS.CATALOG}.threatactor`],
  THREAT_ACTOR_ID: [`${BASE_PATHS.CATALOG}.threatactor_id`],
  MALWARE_KIT_NAME: [`${BASE_PATHS.CATALOG}.malwarekit`],
  MALWARE_KIT_ID: [`${BASE_PATHS.CATALOG}.malwarekit_id`],
  SOURCE_ID: [`${BASE_PATHS.CATALOG}.source_id`],
  SOURCE_NAME: [`${BASE_PATHS.CATALOG}.source`, `${BASE_PATHS.METADATA}.collector.name`],
  TYPE: [`${BASE_PATHS.CATALOG}.type`],
  INDUSTRY_ID: [`${BASE_PATHS.CATALOG}.industrysector_id`],
  INDUSTRY_NAME: [`${BASE_PATHS.CATALOG}.industrysector`],
  TECHNOLOGY_ID: [`${BASE_PATHS.CATALOG}.technology_id`],
  TECHNOLOGY: [`${BASE_PATHS.CATALOG}.technology`],
  LOCATION_ID: [`${BASE_PATHS.CATALOG}.country_id`],
  LOCATION_NAME: [`${BASE_PATHS.CATALOG}.country`],
  EMAIL: [`${BASE_PATHS.ATTRIBUTES}.email`],
  IP: [`${BASE_PATHS.ATTRIBUTES}.ip`],
  USERNAME: [`${BASE_PATHS.STRUCTURED}.credential.username`],
  DOMAIN: [`${BASE_PATHS.STRUCTURED}.credential.urlinfo.domain`],
  HOSTNAME: [`${BASE_PATHS.STRUCTURED}.credential.urlinfo.hostname`],
  HOSTNAME_RISK_LEVEL: [`${BASE_PATHS.STRUCTURED}.credential.urlinfo.hostname_risk.level`],
  PATH_RISK_LEVEL: [`${BASE_PATHS.STRUCTURED}.credential.urlinfo.path_risk.level`],
  LOCAL_HOSTNAME: [`${BASE_PATHS.STRUCTURED}.metadata.hostname`],
  HARDWARE_ID: [`${BASE_PATHS.STRUCTURED}.metadata.hardware_id`],
  COUNTRY: [`${BASE_PATHS.STRUCTURED}.metadata.country`],
  LOGGED_USERNAME: [`${BASE_PATHS.STRUCTURED}.metadata.logged_in_user`],
  LOCAL_LANGUAGE: [`${BASE_PATHS.STRUCTURED}.metadata.language`],
  LOCATION: [`${BASE_PATHS.STRUCTURED}.metadata.location`],
  IP_ADDRESS: [`${BASE_PATHS.STRUCTURED}.metadata.ip`],
  OPERATING_SYSTEM: [`${BASE_PATHS.STRUCTURED}.metadata.os`],
  LOCAL_TIMESTAMP: [`${BASE_PATHS.STRUCTURED}.metadata.timestamp`],
  LOCAL_TIMEZONE: [`${BASE_PATHS.STRUCTURED}.metadata.timezone`],
  URL: [`${BASE_PATHS.STRUCTURED}.credential.url`],
  PASSWORD_ATTRIBUTE: [`${BASE_PATHS.ATTRIBUTES}.password`],
  PASSWORD: [`${BASE_PATHS.STRUCTURED}.credential.password`],
  LEAKED_PASSWORD: [`${BASE_PATHS.STRUCTURED}.credential.password.starred`],
  HASH: [`${BASE_PATHS.STRUCTURED}.credential.password.hash_sha256`],
  INDEX: ['index'],
};

const moveToObject = (
  data: Record<string, never>,
  { id, name }: { id: Array<string>; name: Array<string> }
): Record<string, any> => {
  const ids = findInObject(data, id, []) as Array<any>;
  const names = findInObject(data, name, []) as Array<any>;

  // return {key: value}f format:
  return ids.reduce((accu, id, index) => {
    accu[id] = names[index];

    return accu;
  }, {});
};

export type NormalizedSignal = ReturnType<typeof normalizeSignal>;

const getCredentialType = (signal: Signal) => {
  const isAdmin = findInObject(signal, [`${BASE_PATHS.STRUCTURED}.credential.user.is_admin`]);
  const highRiskUrl = findInObject(signal, [
    `${BASE_PATHS.STRUCTURED}.credential.urlinfo.path_risk.level`,
  ]);
  const highRiskHost = findInObject(signal, [
    `${BASE_PATHS.STRUCTURED}.credential.urlinfo.hostname_risk.level`,
  ]);

  if (isAdmin) {
    return 'admin_account';
  }

  if (highRiskUrl && highRiskUrl === 'high') {
    return 'high_risk_url';
  }

  if (highRiskHost && highRiskHost === 'high') {
    return 'high_risk_host';
  }

  return '';
};

export const getNormalizedSignalCollector = (collector?: string): any => {
  if (!collector) return null;

  const trimmed = collector.trim();
  if (!trimmed) return null;

  const match = Object.values(CollectorType).find((value) => trimmed.startsWith(value));

  if (match) {
    return match as CollectorType;
  }

  return trimmed.split('-')[0];
};

const normalizeSignalId = (rawId: unknown): string => {
  if (typeof rawId === 'string') {
    return rawId;
  }

  if (typeof rawId === 'number' || typeof rawId === 'bigint') {
    return String(rawId);
  }

  return '';
};

type RiskLevel = 'medium' | 'high';

const normalizeRiskLevel = (value: string): RiskLevel | null => {
  if (value === 'medium' || value === 'high') return value;
  return null;
};

export const normalizeSignal = (signal: Signal) => ({
  id: normalizeSignalId(
    (signal.signal_id as any) || (findInObject(signal, SIGNALS_PATHS.ID) as any)
  ),
  title: findInObject(signal, SIGNALS_PATHS.TITLE),
  text: findInObject(signal, SIGNALS_PATHS.TEXT),
  ai_summary: findInObject(signal, SIGNALS_PATHS.AI_SUMMARY),
  summary: findInObject(signal, SIGNALS_PATHS.SUMMARY),
  translations: findInObject(signal, SIGNALS_PATHS.TRANSLATIONS),
  translated_text: findInObject(signal, SIGNALS_PATHS.TRANSLATED_TEXT),
  language: findInObject(signal, SIGNALS_PATHS.LANGUAGE),
  timestamp: findInObject(signal, SIGNALS_PATHS.TIMESTAMP),
  published_at: findInObject(signal, SIGNALS_PATHS.PUBLISHED_AT),
  updated_at: findInObject(signal, SIGNALS_PATHS.UPDATED_AT),
  ingested_at: findInObject(signal, SIGNALS_PATHS.INGESTED_AT),
  link: findInObject(signal, SIGNALS_PATHS.LINK),
  collector: getNormalizedSignalCollector(
    findInObject(signal, SIGNALS_PATHS.INDEX) ?? findInObject(signal, SIGNALS_PATHS.COLLECTOR_NAME)
  ),
  credentials: findInObject(signal, SIGNALS_PATHS.PASSWORD),
  hardwareId: findInObject(signal, SIGNALS_PATHS.HARDWARE_ID),
  country: findInObject(signal, SIGNALS_PATHS.COUNTRY),
  loggedUsername: findInObject(signal, SIGNALS_PATHS.LOGGED_USERNAME),
  localLanguage: findInObject(signal, SIGNALS_PATHS.LOCAL_LANGUAGE),
  location: findInObject(signal, SIGNALS_PATHS.LOCATION),
  ipAddress: findInObject(signal, SIGNALS_PATHS.IP_ADDRESS),
  operatingSystem: findInObject(signal, SIGNALS_PATHS.OPERATING_SYSTEM),
  localTimestamp: findInObject(signal, SIGNALS_PATHS.LOCAL_TIMESTAMP),
  localTimezone: findInObject(signal, SIGNALS_PATHS.LOCAL_TIMEZONE),
  credential: {
    username: findInObject(signal, SIGNALS_PATHS.USERNAME),
    domain: findInObject(signal, SIGNALS_PATHS.DOMAIN),
    hostname: findInObject(signal, SIGNALS_PATHS.HOSTNAME),
    localHostname: findInObject(signal, SIGNALS_PATHS.LOCAL_HOSTNAME),
    url: findInObject(signal, SIGNALS_PATHS.URL),
    type: getCredentialType(signal),
    leaked_password: findInObject(signal, SIGNALS_PATHS.LEAKED_PASSWORD),
    hash: findInObject(signal, SIGNALS_PATHS.HASH),
    hostnameRiskLevel: normalizeRiskLevel(findInObject(signal, SIGNALS_PATHS.HOSTNAME_RISK_LEVEL)),
    pathRiskLevel: normalizeRiskLevel(findInObject(signal, SIGNALS_PATHS.PATH_RISK_LEVEL)),
  },
  attributes: {
    email: findInObject(signal, SIGNALS_PATHS.EMAIL),
    ip: findInObject(signal, SIGNALS_PATHS.IP),
    password: findInObject(signal, SIGNALS_PATHS.PASSWORD_ATTRIBUTE),
  },
  author: {
    jobtitle: findInObject(signal, SIGNALS_PATHS.AUTHOR_JOB_TITLE),
    forum: findInObject(signal, SIGNALS_PATHS.FORUM_AUTHOR),
    telegram: findInObject(signal, SIGNALS_PATHS.TELEGRAM_AUTHOR),
    name: findInObject(signal, SIGNALS_PATHS.FORUM_AUTHOR),
  },
  thread: {
    title: findInObject(signal, SIGNALS_PATHS.THREAD_TITLE),
    path: findInObject(signal, SIGNALS_PATHS.THREAD_PATH),
  },
  channel: {
    title: findInObject(signal, SIGNALS_PATHS.CHANNEL_TITLE),
  },
  catalog: {
    category: findInObject(signal, SIGNALS_PATHS.CATEGORY),
    vulnerability: moveToObject(signal, {
      id: SIGNALS_PATHS.VULNERABILITY_ID,
      name: SIGNALS_PATHS.VULNERABILITY_NAME,
    }),
    threatactor: moveToObject(signal, {
      id: SIGNALS_PATHS.THREAT_ACTOR_ID,
      name: SIGNALS_PATHS.THREAT_ACTOR_NAME,
    }),
    malwarekit: moveToObject(signal, {
      id: SIGNALS_PATHS.MALWARE_KIT_ID,
      name: SIGNALS_PATHS.MALWARE_KIT_NAME,
    }),
    source: findInObject(signal, SIGNALS_PATHS.SOURCE_NAME),
    source_id: findInObject(signal, SIGNALS_PATHS.SOURCE_ID),
    technologies: moveToObject(signal, {
      id: SIGNALS_PATHS.TECHNOLOGY_ID,
      name: SIGNALS_PATHS.TECHNOLOGY,
    }),
    locations: moveToObject(signal, {
      id: SIGNALS_PATHS.LOCATION_ID,
      name: SIGNALS_PATHS.LOCATION_NAME,
    }),
    industry_sectors: moveToObject(signal, {
      id: SIGNALS_PATHS.INDUSTRY_ID,
      name: SIGNALS_PATHS.INDUSTRY_NAME,
    }),
  },
  raw: signal,
  index: findInObject(signal, SIGNALS_PATHS.INDEX),
});

export const signalQuery = (id: string) => ({
  queryKey: unifiedApiKeys.ticket('/signals', id),
  queryFn: () =>
    openApiFetch('/signals/{resource_id}', {
      params: {
        path: { resource_id: id },
      },
      headers: { source: 'entity' },
    }),
  select: (raw: any) => {
    const data = raw as SignalsSignalFetchResponse;

    return normalizeSignal(data as any);
  },
});

export const useSignalEntity = (id: string) => useQuery(signalQuery(id));

export const useSignals = (params: Partial<UnifiedApiParams>) => {
  const list = <TResult = SignalsFetchResponse>(select?: (data: SignalsFetchResponse) => TResult) =>
    useQuery({
      queryKey: unifiedApiKeys.records('/signals', params),
      queryFn: async () => openapiClient.GET('/signals', { params: { query: params } }),
      select,
      placeholderData: keepPreviousData,
    });

  const summary = () =>
    useQuery({
      queryKey: unifiedApiKeys.summary('/signals/summary', params),
      queryFn: async () => openapiClient.GET('/signals/summary', { params: { query: params } }),
      select: (data) => ({
        ...data,
        area: data.data
          ? getEntries(data.data?.signal_timeline).map(([key, value]) => [+new Date(key), value])
          : [],
      }),
      placeholderData: keepPreviousData,
    });

  return {
    summary,
    list,
  };
};

export const signalUrl = (id: string): string => {
  const normalizedId = normalizeSignalId(id);

  if (!normalizedId || normalizedId.trim() === '') {
    throw new Error('signalUrl: id must be a non-empty string');
  }

  return `/signal/${normalizedId}`;
};

export const signalSourceUrl = (link: unknown): string | null => {
  if (typeof link !== 'string') return null;

  const trimmed = link.trim();

  try {
    const { protocol } = new URL(trimmed);

    return protocol === 'http:' || protocol === 'https:' ? trimmed : null;
  } catch {
    return null;
  }
};
